import "./CompanyCashRegister.css";

import CashClosingReport from "./components/CashClosingReport";
import JournalAuditModal from "./components/JournalAuditModal";

import { usePermissions } from "./PermissionContext";
import { openCashClosingPrintWindow } from "./printing/CashClosingPrinter";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import axios from "axios";
import { toast } from "react-toastify";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  API_URL,
  getPosHeaders,
  getPosSession,
} from "./api";


const initialOpenData = {
  opening_amount: "",
  opening_note: "",
};

const initialMovementData = {
  movement_type: "income",
  amount: "",
  concept: "",
  note: "",
};

const initialCloseData = {
  closing_note: "",
};

const MOVEMENT_LABELS = {
  opening: "Apertura",
  income: "Ingreso",
  sale_cash: "Venta POS",
  expense: "Egreso",
  refund_cash: "Devolución",
  adjustment: "Ajuste",
};

const CASH_DENOMINATIONS = [
  100000,
  50000,
  20000,
  10000,
  5000,
  2000,
  1000,
  500,
  200,
  100,
  50,
];


function formatCurrency(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}


function getErrorMessage(error, fallback) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    const message = detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");

    if (message) return message;
  }

  return fallback;
}


function CompanyCashRegister() {
  const [cashSession, setCashSession] = useState(null);
  const [cashSummary, setCashSummary] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  const [closingReport, setClosingReport] = useState(null);

  const [
    showCloseConfirmation,
    setShowCloseConfirmation,
  ] = useState(false);

  const [isClosing, setIsClosing] = useState(false);

  const [
    linkedJournalEntry,
    setLinkedJournalEntry,
  ] = useState(null);

  const [
    auditJournalEntry,
    setAuditJournalEntry,
  ] = useState(null);

  const [openData, setOpenData] = useState(initialOpenData);

  const [movementData, setMovementData] =
    useState(initialMovementData);

  const [closeData, setCloseData] =
    useState(initialCloseData);

  const [denominationCounts, setDenominationCounts] =
    useState({});

  const closingInProgress = useRef(false);

  const navigate = useNavigate();
  const { company } = useParams();

  const getHeaders = useCallback(
    () => ({
      headers: getPosHeaders(company),
    }),
    [company]
  );

  const { can } = usePermissions();

  const canManageCash = can("cash.manage");
  const canViewAudit = can("accounting.audit");

  // Las denominaciones corresponden únicamente
  // al efectivo de la operación, sin saldo de apertura.
  const countedAmount = CASH_DENOMINATIONS.reduce(
    (total, denomination) =>
      total +
      denomination *
      Number(denominationCounts[denomination] || 0),
    0
  );

  const openingAmount = Number(
    cashSummary?.opening_amount ??
    cashSession?.opening_amount ??
    0
  );

  // El backend conserva el esperado físico completo.
  // Para comparar la operación, descontamos la apertura.
  const expectedWithoutOpening = Number(
    (
      Number(
        cashSummary?.expected_amount ??
        cashSession?.expected_amount ??
        openingAmount
      ) - openingAmount
    ).toFixed(2)
  );

  const estimatedDifference = Number(
    (countedAmount - expectedWithoutOpening).toFixed(2)
  );

  // Total físico declarado: operación + saldo de apertura.
  const totalCashWithOpening = Number(
    (countedAmount + openingAmount).toFixed(2)
  );

  const fetchCashData = useCallback(async () => {
    try {
      const sessionResponse = await axios.get(
        `${API_URL}/pos/cash-register/me/current`,
        getHeaders()
      );

      const { is_open, session } = sessionResponse.data;

      if (!is_open) {
        setCashSession(null);
        setCashSummary(null);
        setMovements([]);
        return;
      }

      setCashSession(session);

      const summaryResponse = await axios.get(
        `${API_URL}/pos/cash-register/me/summary`,
        getHeaders()
      );

      setCashSummary(summaryResponse.data);

      const movementsResponse = await axios.get(
        `${API_URL}/pos/cash-register/me/movements`,
        getHeaders()
      );

      setMovements(movementsResponse.data);
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "Error cargando la caja."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchCashData();
  }, [fetchCashData]);

  const handleOpenChange = (event) => {
    const { name, value } = event.target;

    setOpenData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleMovementChange = (event) => {
    const { name, value } = event.target;

    setMovementData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleCloseChange = (event) => {
    const { name, value } = event.target;

    setCloseData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleDenominationChange = (denomination, value) => {
    if (value === "") {
      setDenominationCounts((previous) => {
        const next = { ...previous };

        delete next[denomination];

        return next;
      });

      return;
    }

    const quantity = Math.max(
      0,
      Number.parseInt(value, 10) || 0
    );

    setDenominationCounts((previous) => ({
      ...previous,
      [denomination]: quantity,
    }));
  };

  const openCashRegister = async (event) => {
    event.preventDefault();

    if (!canManageCash) {
      toast.error("No tienes permiso para abrir caja.");
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/pos/cash-register/me/open`,
        {
          opening_amount: Number(openData.opening_amount),
          opening_note: openData.opening_note || null,
        },
        getHeaders()
      );

      toast.success(response.data.message);

      setOpenData(initialOpenData);
      setCloseData(initialCloseData);
      setClosingReport(null);
      setDenominationCounts({});

      await fetchCashData();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "No fue posible abrir la caja."
        )
      );
    }
  };

  const createMovement = async (event) => {
    event.preventDefault();

    if (!canManageCash) {
      toast.error(
        "No tienes permiso para registrar movimientos de caja."
      );
      return;
    }

    try {
      await axios.post(
        `${API_URL}/pos/cash-register/me/movements`,
        {
          movement_type: movementData.movement_type,
          amount: Number(movementData.amount),
          concept: movementData.concept,
          note: movementData.note || null,
        },
        getHeaders()
      );

      toast.success(
        movementData.movement_type === "income"
          ? "Ingreso registrado correctamente."
          : "Egreso registrado correctamente."
      );

      setMovementData(initialMovementData);

      await fetchCashData();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "No fue posible registrar el movimiento."
        )
      );
    }
  };

  // Respaldo si no puede consultarse el reporte
  // detallado después de confirmar el cierre.
  const createClosingReport = (closedSession) => {
    const companyInfo = getPosSession(company) || {};

    const totalByType = (type) =>
      movements
        .filter(
          (movement) => movement.movement_type === type
        )
        .reduce(
          (total, movement) =>
            total + Number(movement.amount || 0),
          0
        );

    return {
      company_name:
        companyInfo.company_name || "Mi empresa",

      company_logo:
        companyInfo.logo ||
        companyInfo.company_logo ||
        companyInfo.logo_url ||
        null,

      company_nit: companyInfo.nit || null,
      company_email: companyInfo.email || null,
      company_phone: companyInfo.phone || null,
      company_address: companyInfo.address || null,

      session_id: closedSession.id,
      opened_at: closedSession.opened_at,
      closed_at: closedSession.closed_at,

      opened_by:
        closedSession.opened_by_name || "Sistema",

      closed_by:
        closedSession.closed_by_name || "Sistema",

      opening_amount: closedSession.opening_amount,

      sales_income:
        cashSummary?.sales_income ??
        totalByType("sale_cash"),

      manual_income:
        cashSummary?.manual_income ??
        totalByType("income"),

      manual_expense:
        cashSummary?.manual_expense ??
        totalByType("expense"),

      refunds:
        cashSummary?.refunds ??
        totalByType("refund_cash"),

      expected_amount: closedSession.expected_amount,
      counted_amount: closedSession.counted_amount,
      difference_amount: closedSession.difference_amount,

      closing_note: closeData.closing_note || null,

      // No presentar las denominaciones de la operación
      // como si fueran el desglose del efectivo físico total.
      denominations: [],

      movements: movements
        .filter(
          (movement) =>
            movement.movement_type !== "opening"
        )
        .map((movement) => ({
          id: movement.id,
          created_at: movement.created_at,
          date: movement.created_at,
          type:
            MOVEMENT_LABELS[movement.movement_type] ||
            movement.movement_type,
          movement_type: movement.movement_type,
          concept: movement.concept,
          amount: Number(movement.amount || 0),
        })),
    };
  };

  const requestCashClosing = (event) => {
    event.preventDefault();

    if (!canManageCash) {
      toast.error("No tienes permiso para cerrar caja.");
      return;
    }

    if (
      !Number.isFinite(countedAmount) ||
      countedAmount < 0 ||
      !Number.isFinite(totalCashWithOpening) ||
      totalCashWithOpening < 0
    ) {
      toast.error(
        "Revisa las cantidades registradas en el arqueo."
      );
      return;
    }

    if (!cashSummary || !cashSession) {
      toast.error(
        "Espera a que se cargue el resumen de la caja."
      );
      return;
    }

    setShowCloseConfirmation(true);
  };

  const confirmCashClosing = async () => {
    if (closingInProgress.current) return;

    if (!canManageCash || !cashSession || !cashSummary) {
      toast.error(
        "No puedes cerrar esta caja en este momento."
      );
      return;
    }

    if (
      !Number.isFinite(totalCashWithOpening) ||
      totalCashWithOpening < 0
    ) {
      toast.error(
        "Revisa el efectivo registrado antes de cerrar."
      );
      return;
    }

    closingInProgress.current = true;
    setIsClosing(true);

    try {
      const response = await axios.post(
        `${API_URL}/pos/cash-register/me/close`,
        {
          // El servidor recibe el total físico:
          // efectivo de la operación + apertura.
          counted_amount: totalCashWithOpening,
          closing_note: closeData.closing_note || null,
          denominations: [],
        },
        getHeaders()
      );

      const closedSession = response.data.session;

      const difference = Number(
        closedSession.difference_amount || 0
      );

      const fallbackReport =
        createClosingReport(closedSession);

      setShowCloseConfirmation(false);

      try {
        const reportResponse = await axios.get(
          `${API_URL}/pos/cash-register/report/${closedSession.id}`,
          getHeaders()
        );

        setClosingReport({
          ...reportResponse.data,

          company_logo:
            reportResponse.data.company_logo ||
            fallbackReport.company_logo,

          company_address:
            reportResponse.data.company_address ||
            fallbackReport.company_address,
        });
      } catch {
        setClosingReport({
          ...fallbackReport,

          report_warnings: [
            "No se pudo cargar el detalle de productos y medios " +
            "de pago. Consulta nuevamente este cierre desde " +
            "Cierres de caja antes de imprimir el reporte completo.",
          ],
        });

        toast.warning(
          "La caja quedó cerrada, pero no se pudo cargar " +
          "el reporte detallado. Puedes consultarlo desde " +
          "Cierres de caja."
        );
      }

      setCloseData(initialCloseData);
      setDenominationCounts({});

      if (difference === 0) {
        toast.success(
          "Caja cerrada correctamente. El arqueo está cuadrado."
        );
      } else {
        toast.success(
          `Caja cerrada con una diferencia de ${formatCurrency(
            Math.abs(difference)
          )}.`
        );
      }

      await fetchCashData();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "No fue posible cerrar la caja."
        )
      );
    } finally {
      closingInProgress.current = false;
      setIsClosing(false);
    }
  };

  const handlePrintClosing = () => {
    if (!closingReport) return;

    if (!Array.isArray(closingReport.sold_products)) {
      toast.warning(
        "Consulta este cierre desde Cierres de caja " +
        "para obtener e imprimir el reporte completo."
      );
      return;
    }

    const printed = openCashClosingPrintWindow(
      closingReport,
      {
        company,
      }
    );

    if (!printed) {
      toast.error(
        "No fue posible abrir la ventana de impresión. " +
        "Verifica que el navegador no bloquee ventanas emergentes."
      );
    }
  };

  if (loading) {
    return <h2 role="status">Cargando caja...</h2>;
  }

  return (
    <div className="company-cash-register">
      <div className="cash-heading">
        <div>
          <h1>Caja</h1>

          <p>
            Registra movimientos de efectivo y realiza
            el arqueo diario.
          </p>
        </div>

        <span
          className={`cash-status ${cashSession
              ? "status-open"
              : "status-closed"
            }`}
        >
          {cashSession ? "Caja abierta" : "Caja cerrada"}
        </span>
      </div>

      {!cashSession && (
        <section className="cash-card open-cash-card">
          <h2>Abrir caja</h2>

          <p>
            Ingresa el dinero físico disponible antes
            de iniciar ventas.
          </p>

          <form onSubmit={openCashRegister}>
            <div className="cash-form-grid">
              <div className="cash-field">
                <label htmlFor="opening_amount">
                  Monto inicial
                </label>

                <input
                  type="number"
                  id="opening_amount"
                  name="opening_amount"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={openData.opening_amount}
                  onChange={handleOpenChange}
                  required
                />
              </div>

              <div className="cash-field">
                <label htmlFor="opening_note">
                  Observación
                </label>

                <input
                  type="text"
                  id="opening_note"
                  name="opening_note"
                  placeholder="Ejemplo: Dinero entregado por administración"
                  value={openData.opening_note}
                  onChange={handleOpenChange}
                />
              </div>
            </div>

            {canManageCash ? (
              <button
                className="btn-open-cash"
                type="submit"
              >
                Abrir caja
              </button>
            ) : (
              <div className="cash-readonly-message">
                No tienes permiso para abrir caja.
              </div>
            )}
          </form>
        </section>
      )}

      {cashSession && (
        <>
          <section className="cash-summary-grid">
            <article className="cash-summary-card">
              <span>Base inicial</span>

              <strong>
                {formatCurrency(openingAmount)}
              </strong>
            </article>
            <article className="cash-summary-card">
              <span>Ventas en efectivo</span>

              <strong>
                {formatCurrency(cashSummary?.sales_income)}
              </strong>
            </article>

            <article className="cash-summary-card">
              <span>Ingresos manuales</span>

              <strong>
                {formatCurrency(cashSummary?.manual_income)}
              </strong>
            </article>

            <article className="cash-summary-card">
              <span>Egresos manuales</span>

              <strong>
                {formatCurrency(cashSummary?.manual_expense)}
              </strong>
            </article>

            <article className="cash-summary-card expected">
              <span>Efectivo de caja</span>

              <strong>
                {formatCurrency(expectedWithoutOpening)}
              </strong>
            </article>

            <article className="cash-summary-card">
              <span>Movimientos</span>

              <strong>
                {cashSummary?.movements ?? 0}
              </strong>
            </article>
          </section>

          <section className="cash-card movement-card">
            <h2>Registrar movimiento</h2>

            <p>
              Usa ingresos y egresos manuales solo para
              operaciones que no correspondan a una venta.
            </p>

            <form onSubmit={createMovement}>
              <div className="cash-form-grid movement-grid">
                <div className="cash-field">
                  <label htmlFor="movement_type">
                    Tipo
                  </label>

                  <select
                    id="movement_type"
                    name="movement_type"
                    value={movementData.movement_type}
                    onChange={handleMovementChange}
                    disabled={isClosing}
                  >
                    <option value="income">Ingreso</option>
                    <option value="expense">Egreso</option>
                  </select>
                </div>

                <div className="cash-field">
                  <label htmlFor="amount">
                    Valor
                  </label>

                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    min="0.01"
                    step="0.01"
                    placeholder="0"
                    value={movementData.amount}
                    onChange={handleMovementChange}
                    disabled={isClosing}
                    required
                  />
                </div>

                <div className="cash-field cash-field-wide">
                  <label htmlFor="concept">
                    Concepto
                  </label>

                  <input
                    type="text"
                    id="concept"
                    name="concept"
                    placeholder="Ejemplo: Pago de transporte"
                    value={movementData.concept}
                    onChange={handleMovementChange}
                    disabled={isClosing}
                    required
                  />
                </div>
              </div>

              <div className="cash-field">
                <label htmlFor="note">
                  Nota adicional
                </label>

                <textarea
                  id="note"
                  name="note"
                  rows="3"
                  placeholder="Información adicional (opcional)"
                  value={movementData.note}
                  onChange={handleMovementChange}
                  disabled={isClosing}
                />
              </div>

              {canManageCash ? (
                <button
                  className={
                    movementData.movement_type === "income"
                      ? "btn-income"
                      : "btn-expense"
                  }
                  type="submit"
                  disabled={isClosing}
                >
                  {movementData.movement_type === "income"
                    ? "Registrar ingreso"
                    : "Registrar egreso"}
                </button>
              ) : (
                <div className="cash-readonly-message">
                  No tienes permiso para registrar
                  movimientos de caja.
                </div>
              )}
            </form>
          </section>

          <section className="cash-card close-cash-card">
            <h2>Arqueo y cierre de caja</h2>

            <p>
              Registra únicamente el efectivo de la operación.
              Separa el dinero entregado al abrir la caja:
              el sistema lo agregará al total final.
            </p>

            <form onSubmit={requestCashClosing}>
              <div className="cash-denominations">
                <div className="cash-denominations-header">
                  <div>
                    <h3>Conteo por denominación</h3>

                    <p>
                      Registra los billetes y monedas
                      correspondientes al efectivo de la
                      operación, sin incluir el saldo de apertura.
                    </p>
                  </div>
                </div>

                <div className="cash-denominations-grid">
                  {CASH_DENOMINATIONS.map((denomination) => (
                    <label
                      className="cash-denomination-item"
                      key={denomination}
                    >
                      <span>
                        {formatCurrency(denomination)}
                      </span>

                      <input
                        type="number"
                        min="0"
                        step="1"
                        inputMode="numeric"
                        aria-label={`Cantidad de ${formatCurrency(
                          denomination
                        )}`}
                        placeholder="0"
                        value={
                          denominationCounts[denomination] ?? ""
                        }
                        onChange={(event) =>
                          handleDenominationChange(
                            denomination,
                            event.target.value
                          )
                        }
                        disabled={isClosing}
                      />

                      <small>
                        {formatCurrency(
                          denomination *
                          Number(
                            denominationCounts[denomination] || 0
                          )
                        )}
                      </small>
                    </label>
                  ))}
                </div>
              </div>

              <div className="cash-form-grid">
                <div className="cash-field cash-field-wide">
                  <label htmlFor="closing_note">
                    Observación de cierre
                  </label>

                  <input
                    type="text"
                    id="closing_note"
                    name="closing_note"
                    placeholder="Ejemplo: Diferencia por redondeo"
                    value={closeData.closing_note}
                    onChange={handleCloseChange}
                    disabled={isClosing}
                  />
                </div>
              </div>

              <div className="cash-counting-preview">
                <div>
                  <span>Efectivo de caja</span>

                  <strong>
                    {formatCurrency(expectedWithoutOpening)}
                  </strong>
                </div>

                <div>
                  <span>Diferencia estimada</span>

                  <strong
                    className={
                      estimatedDifference === 0
                        ? "difference-balanced"
                        : "difference-pending"
                    }
                  >
                    {formatCurrency(estimatedDifference)}
                  </strong>
                </div>
              </div>

              <div
                className="cash-close-modal-summary"
                aria-live="polite"
                aria-atomic="true"
              >
                <div style={{ gridColumn: "1 / -1" }}>
                  <span>
                    Efectivo contado + saldo inicial de caja
                  </span>

                  <strong>
                    {formatCurrency(totalCashWithOpening)}
                  </strong>
                </div>
              </div>

              <p>
                <small>
                  El efectivo de caja incluye ventas en efectivo,
                  ingresos y abonos, menos egresos y devoluciones.
                  Los pagos por tarjeta, transferencia y crédito
                  no forman parte de este conteo.
                </small>
              </p>

              {canManageCash ? (
                <button
                  className="btn-close-cash"
                  type="submit"
                  disabled={isClosing || !cashSummary}
                >
                  Realizar arqueo y cerrar caja
                </button>
              ) : (
                <div className="cash-readonly-message">
                  No tienes permiso para realizar
                  arqueos y cerrar caja.
                </div>
              )}
            </form>
          </section>
        </>
      )}

      {closingReport && (
        <section className="cash-card closing-report-panel">
          <div className="closing-report-header">
            <div>
              <h2>Arqueo cerrado</h2>
              <p>El cierre fue registrado correctamente.</p>
            </div>

            <button
              type="button"
              className="btn-print-closing"
              onClick={handlePrintClosing}
            >
              Imprimir arqueo
            </button>
          </div>

          <div className="closing-report-print">
            <CashClosingReport report={closingReport} />
          </div>
        </section>
      )}

      {showCloseConfirmation && (
        <div
          className="cash-close-modal-backdrop"
          onClick={() => {
            if (!isClosing) {
              setShowCloseConfirmation(false);
            }
          }}
        >
          <section
            className="cash-close-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cash-close-confirmation-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="cash-close-modal-icon">
              ✓
            </div>

            <p className="cash-close-modal-eyebrow">
              CONFIRMACIÓN DE CIERRE
            </p>

            <h2 id="cash-close-confirmation-title">
              ¿Confirmar arqueo de caja?
            </h2>

            <p>
              Se registrará el cierre de la caja actual.
              Esta operación no puede deshacerse.
            </p>

            <div className="cash-close-modal-summary">
              <div>
                <span>Efectivo de caja</span>

                <strong>
                  {formatCurrency(expectedWithoutOpening)}
                </strong>
              </div>

              <div>
                <span>Diferencia</span>

                <strong
                  className={
                    estimatedDifference === 0
                      ? "difference-balanced"
                      : "difference-pending"
                  }
                >
                  {formatCurrency(estimatedDifference)}
                </strong>
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <span>
                  Efectivo contado + saldo inicial de caja
                </span>

                <strong>
                  {formatCurrency(totalCashWithOpening)}
                </strong>
              </div>
            </div>

            <p>
              <small>
                Confirma que el saldo de apertura está
                separado y disponible. Se sumará al efectivo
                de la operación para registrar el total final.
              </small>
            </p>

            <div className="cash-close-modal-actions">
              <button
                type="button"
                className="btn-cash-secondary"
                disabled={isClosing}
                onClick={() =>
                  setShowCloseConfirmation(false)
                }
              >
                Volver
              </button>

              <button
                type="button"
                className="btn-confirm-cash-close"
                disabled={isClosing}
                onClick={confirmCashClosing}
              >
                {isClosing
                  ? "Cerrando caja..."
                  : "Confirmar cierre"}
              </button>
            </div>
          </section>
        </div>
      )}

      {linkedJournalEntry && (
        <div
          className="cash-linked-entry-backdrop"
          onClick={() => setLinkedJournalEntry(null)}
        >
          <section
            className="cash-linked-entry-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Comprobante contable asociado</h3>

            <p>
              <strong>Número:</strong>{" "}
              {linkedJournalEntry.number}
            </p>

            <p>
              <strong>Fecha:</strong>{" "}
              {new Date(
                `${linkedJournalEntry.entry_date}T00:00:00`
              ).toLocaleDateString("es-CO")}
            </p>

            <p>
              <strong>Descripción:</strong>{" "}
              {linkedJournalEntry.description}
            </p>

            <p>
              <strong>Estado:</strong>{" "}
              {linkedJournalEntry.status}
            </p>

            <div className="cash-linked-entry-actions">
              {canViewAudit && (
                <button
                  type="button"
                  className="btn-cash-accounting"
                  onClick={() =>
                    setAuditJournalEntry(linkedJournalEntry)
                  }
                >
                  Auditoría
                </button>
              )}

              <button
                type="button"
                className="btn-cash-accounting"
                onClick={() =>
                  navigate(`/${company}/contabilidad`)
                }
              >
                Abrir Contabilidad
              </button>

              <button
                type="button"
                className="btn-cash-secondary"
                onClick={() => setLinkedJournalEntry(null)}
              >
                Cerrar
              </button>
            </div>
          </section>
        </div>
      )}

      {auditJournalEntry && (
        <JournalAuditModal
          journalEntry={auditJournalEntry}
          onClose={() => setAuditJournalEntry(null)}
        />
      )}
    </div>
  );
}

export default CompanyCashRegister;